import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import styles from "./DiscountProductPage.module.css";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPercent,
  faFilter,
  faSearch,
  faTimes,
  faArrowRight,
  faArrowLeft,
  faTags,
  faSlidersH,
  faLayerGroup,
  faPalette,
  faDollarSign,
  faStar,
  faFire,
  faClock,
  faSortAmountDown,
  faEraser,
  faChevronDown,
  faCheckCircle,
  faAngleDoubleDown,
  faAngleUp,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import DiscountProductCard from "./DiscountProductCard";
import { getDiscountedProducts } from "../../service/product";
import PriceRangeSlider from "../../components/PriceRangeSlider";
import SeriesFilter from "../../components/SeriesFilter";
import ReactPaginate from "react-paginate";
import { Button } from "react-bootstrap";
import classNames from "classnames";

// Type definitions
interface FilterOption {
  value: string;
  label: string;
  count: number;
}

interface AvailableFilters {
  colors: FilterOption[];
  series: FilterOption[];
  priceRange: {
    minPrice: number;
    maxPrice: number;
    minDiscount: number;
    maxDiscount: number;
  };
}

interface DiscountRange {
  value: string;
  label: string;
  icon: any;
  color: string;
  min: number;
  max: number;
}

interface SortOption {
  value: string;
  label: string;
  icon: any;
}

interface QuickFilterPreset {
  label: string;
  discountRange?: string;
  sortOption: string;
  icon: any;
  color: string;
  priceRange?: number[];
}

interface Product {
  _id: string;
  name: string;
  price: number;
  ourPrice: number;
  discountPercentage: number;
  color?: string;
  series?: {
    _id: string;
    id: string;
    title: string;
  };
  picture: Array<{
    path: string;
    name: string;
  }>;
  description: string;
}

const DiscountProductPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("discount_desc");
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters>({
    colors: [],
    series: [],
    priceRange: {
      minPrice: 0,
      maxPrice: 100000,
      minDiscount: 0,
      maxDiscount: 100,
    },
  });
  const [showFilters, setShowFilters] = useState(true);
  // Debounce refs
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadRef = useRef(true);

  // Constants
  const discountRanges: DiscountRange[] = useMemo(
    () => [
      {
        value: "all",
        label: "All Discounts",
        icon: faTags,
        color: "#6B7280",
        min: 0,
        max: 100,
      },
      {
        value: "10-20",
        label: "10-20% OFF",
        icon: faPercent,
        color: "#F59E0B",
        min: 10,
        max: 20,
      },
      {
        value: "20-40",
        label: "20-40% OFF",
        icon: faFire,
        color: "#F97316",
        min: 20,
        max: 40,
      },
      {
        value: "40-60",
        label: "40-60% OFF",
        icon: faFire,
        color: "#EF4444",
        min: 40,
        max: 60,
      },
      {
        value: "60+",
        label: "60%+ OFF",
        icon: faFire,
        color: "#DC2626",
        min: 60,
        max: 100,
      },
    ],
    []
  );

  const [selectedDiscountRange, setSelectedDiscountRange] = useState("all");

  const sortOptions: SortOption[] = useMemo(
    () => [
      { value: "discount_desc", label: "Highest Discount", icon: faPercent },
      { value: "discount_asc", label: "Lowest Discount", icon: faPercent },
      {
        value: "ourPrice_desc",
        label: "Price: High to Low",
        icon: faDollarSign,
      },
      {
        value: "ourPrice_asc",
        label: "Price: Low to High",
        icon: faDollarSign,
      },
      { value: "popularity_desc", label: "Most Popular", icon: faStar },
      { value: "newest", label: "Newest First", icon: faClock },
    ],
    []
  );

  const quickFilterPresets: QuickFilterPreset[] = useMemo(
    () => [
      {
        label: "Best Deals",
        discountRange: "40-60",
        sortOption: "discount_desc",
        icon: faFire,
        color: "#EF4444",
      },
      {
        label: "Under ₹10K",
        priceRange: [0, 10000],
        sortOption: "ourPrice_asc",
        icon: faDollarSign,
        color: "#10B981",
      },
      {
        label: "Most Popular",
        sortOption: "popularity_desc",
        icon: faStar,
        color: "#F59E0B",
      },
    ],
    []
  );

  // Calculate applied filters count
  useEffect(() => {
    let count = 0;
    if (searchQuery) count++;
    if (sortOption !== "discount_desc") count++;
    if (selectedDiscountRange !== "all") count++;
    if (
      priceRange[0] > availableFilters.priceRange.minPrice ||
      priceRange[1] < availableFilters.priceRange.maxPrice
    )
      count++;
    if (selectedSeries) count++;
    if (selectedColors.length > 0) count++;
    setAppliedFiltersCount(count);
  }, [
    searchQuery,
    sortOption,
    selectedDiscountRange,
    priceRange,
    selectedSeries,
    selectedColors,
    availableFilters,
  ]);

  // Optimized fetchProducts function
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);

      const selectedDiscount = discountRanges.find(
        (range) => range.value === selectedDiscountRange
      );

      const params: Record<string, any> = {
        pageNumber: currentPage + 1,
        pageSize,
        search: searchQuery,
        sortBy: sortOption.includes("discount")
          ? "discountPercentage"
          : sortOption.includes("ourPrice")
          ? "ourPrice"
          : sortOption.includes("popularity")
          ? "popularity"
          : "createdAt",
        sortOrder: sortOption.includes("_desc") ? "desc" : "asc",
        minPrice: priceRange[0],
        maxPrice: priceRange[1],
        seriesId: selectedSeries,
      };

      if (selectedColors.length > 0) {
        params.color = selectedColors;
      }

      if (selectedDiscount && selectedDiscount.min > 0) {
        params.minDiscount = selectedDiscount.min;
      }

      if (selectedDiscount && selectedDiscount.max < 100) {
        params.maxDiscount = selectedDiscount.max;
      }

      // Clean up params
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === "") {
          delete params[key];
        }
      });

      const data = await getDiscountedProducts(params);

      if (data?.success) {
        setProducts(data.data || []);
        setTotalProducts(data.pagination?.total || 0);

        if (data.filters?.available) {
          const newAvailableFilters: AvailableFilters = {
            colors: data.filters.available.colors || [],
            series: data.filters.available.series || [],
            priceRange: data.filters.available.priceRange || {
              minPrice: 0,
              maxPrice: 100000,
              minDiscount: 0,
              maxDiscount: 100,
            },
          };

          setAvailableFilters(newAvailableFilters);

          if (priceRange[0] === 0 && priceRange[1] === 100000) {
            setPriceRange([
              newAvailableFilters.priceRange.minPrice,
              newAvailableFilters.priceRange.maxPrice,
            ]);
          }
        }
      } else {
        setError("Unexpected data format received from server");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load products");
    } finally {
      setLoading(false);
      initialLoadRef.current = false;
    }
  }, [
    currentPage,
    pageSize,
    searchQuery,
    sortOption,
    priceRange,
    selectedSeries,
    selectedColors,
    selectedDiscountRange,
    discountRanges,
  ]);

  // Main data fetching effect
  useEffect(() => {
    if (initialLoadRef.current) {
      fetchProducts();
    }
  }, [fetchProducts]);

  // Debounced effects for search and filters
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(0);
      fetchProducts();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, sortOption, fetchProducts]);

  useEffect(() => {
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    filterTimeoutRef.current = setTimeout(() => {
      setCurrentPage(0);
      fetchProducts();
    }, 300);

    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [priceRange, fetchProducts]);

  // Immediate effect for other filters
  useEffect(() => {
    setCurrentPage(0);
    fetchProducts();
  }, [
    selectedDiscountRange,
    selectedSeries,
    selectedColors.length,
    fetchProducts,
  ]);

  // Event handlers
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const handlePriceChange = useCallback((values: number[]) => {
    setPriceRange(values);
  }, []);

  const handleDiscountRangeChange = useCallback((rangeValue: string) => {
    setSelectedDiscountRange(rangeValue);
  }, []);

  const handleSeriesChange = useCallback((seriesId: string | null) => {
    setSelectedSeries(seriesId);
  }, []);

  const handleColorChange = useCallback((colorValue: string) => {
    setSelectedColors((prev) =>
      prev.includes(colorValue)
        ? prev.filter((c) => c !== colorValue)
        : [...prev, colorValue]
    );
  }, []);

  const handleSortChange = useCallback((selectedOption: any) => {
    setSortOption(selectedOption.value);
  }, []);

  const handlePageSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setPageSize(Number(e.target.value));
      setCurrentPage(0);
    },
    []
  );

  const handlePageClick = useCallback((selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected);
  }, []);

  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setSortOption("discount_desc");
    setSelectedDiscountRange("all");
    setPriceRange([
      availableFilters.priceRange.minPrice,
      availableFilters.priceRange.maxPrice,
    ]);
    setSelectedSeries(null);
    setSelectedColors([]);
    setCurrentPage(0);
  }, [
    availableFilters.priceRange.minPrice,
    availableFilters.priceRange.maxPrice,
  ]);

  const applyQuickFilter = useCallback((preset: QuickFilterPreset) => {
    setSelectedDiscountRange(preset.discountRange || "all");
    setSortOption(preset.sortOption);
    if (preset.priceRange) {
      setPriceRange(preset.priceRange);
    }
    setCurrentPage(0);
  }, []);

  const clearSelectedColors = useCallback(() => {
    setSelectedColors([]);
  }, []);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    };
  }, []);

  // Memoized color options for react-select
  const colorOptions = useMemo(
    () =>
      availableFilters.colors.map((color) => ({
        value: color.value,
        label: (
          <div className={styles.colorOption}>
            <span className={styles.colorText}>
              {color.label}
              <span className={styles.colorCount}>({color.count})</span>
            </span>
          </div>
        ),
        data: color,
      })),
    [availableFilters.colors]
  );

  const selectedColorValues = useMemo(
    () =>
      selectedColors.map((color) => {
        const colorData = availableFilters.colors.find(
          (c) => c.value === color
        );
        return {
          value: color,
          label: (
            <div className={styles.colorOption}>
              <span className={styles.colorText}>
                {colorData?.label || color}
                <span className={styles.colorCount}>
                  ({colorData?.count || 0})
                </span>
              </span>
            </div>
          ),
        };
      }),
    [selectedColors, availableFilters.colors]
  );

  if (loading && initialLoadRef.current) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <div className={styles.errorMessage}>{error}</div>
        <button
          className={styles.retryButton}
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={styles.discountPage}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroBackground}></div>
        <div className={styles.heroContent}>
          <motion.div
            className={styles.heroBadge}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <FontAwesomeIcon icon={faTags} /> Limited Time Offers
          </motion.div>
          <h1 className={styles.heroTitle}>
            <span className={styles.gradientText}>Exclusive Deals</span>
            <br />
            Up to 70% OFF
          </h1>
          <p className={styles.heroSubtitle}>
            Discover amazing furniture at unbeatable prices with our special
            discounts
          </p>

          {/* Quick Filter Presets */}
          <div className={styles.quickFilterPresets}>
            {quickFilterPresets.map((preset, index) => (
              <motion.button
                key={preset.label}
                className={styles.quickFilterButton}
                onClick={() => applyQuickFilter(preset)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <FontAwesomeIcon icon={preset.icon} />
                {preset.label}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <h2>Hot Deals</h2>
            <p>{totalProducts} products at discounted prices</p>
          </div>
          <div className={styles.headerRight}>
            <h5 onClick={() => setShowFilters(!showFilters)}>
              <FontAwesomeIcon icon={faSlidersH} /> &nbsp;&nbsp;
              {showFilters ? "Close" : "Show"} Filters
              {appliedFiltersCount > 0 && (
                <span className={styles.activeFiltersCount}>
                  ({appliedFiltersCount})
                </span>
              )}
            </h5>
            <div className={styles.resultsCount}>
              Showing {Math.min(currentPage * pageSize + 1, totalProducts)}-
              {Math.min((currentPage + 1) * pageSize, totalProducts)} of{" "}
              {totalProducts} products
            </div>
            {/* {appliedFiltersCount > 0 && (
              <button className={styles.clearAllButton} onClick={resetFilters}>
                <FontAwesomeIcon icon={faEraser} />
                Clear All Filters
              </button>
            )} */}
          </div>
        </div>

        {/* Filter and Content Area */}
        <div
          className={classNames(
            styles.contentGrid,
            !showFilters ? styles.filtershidden : ""
          )}
        >
          {/* Enhanced Filter Sidebar */}
          {showFilters && (
            <aside
              className={`${styles.filterSidebar} ${
                mobileFiltersOpen ? styles.mobileFiltersOpen : ""
              }`}
            >
              <div className={styles.sidebarHeader}>
                <div>
                  <h3>
                    <FontAwesomeIcon icon={faSlidersH} /> Filters
                    {appliedFiltersCount > 0 && (
                      <span className={styles.activeFiltersCount}>
                        ({appliedFiltersCount})
                      </span>
                    )}
                  </h3>
                  <div className={styles.filterActions}>
                    <button
                      className={styles.closeFiltersButton}
                      onClick={() => setMobileFiltersOpen(false)}
                      aria-label="Close filters"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.sidebarContent}>
                {/* Search Section */}
                <div className={styles.filterSection}>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>
                      <FontAwesomeIcon icon={faSearch} /> Search Products
                    </label>
                    <div className={styles.searchBox}>
                      <FontAwesomeIcon
                        icon={faSearch}
                        className={styles.searchIcon}
                      />
                      <input
                        type="text"
                        placeholder="Search furniture, series, keywords..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className={styles.searchInput}
                        aria-label="Search products"
                      />
                    </div>
                  </div>
                </div>

                {/* Discount Range */}
                <div className={styles.filterSection}>
                  <h4 className={styles.filterSectionTitle}>
                    <FontAwesomeIcon icon={faPercent} /> Discount Range
                  </h4>
                  <div className={styles.discountOptions}>
                    {discountRanges.map((range) => (
                      <button
                        key={range.value}
                        className={`${styles.discountOption} ${
                          selectedDiscountRange === range.value
                            ? styles.active
                            : ""
                        }`}
                        onClick={() => handleDiscountRangeChange(range.value)}
                      >
                        <FontAwesomeIcon icon={range.icon} />
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className={styles.filterSection}>
                  <h4 className={styles.filterSectionTitle}>
                    <FontAwesomeIcon icon={faDollarSign} /> Price Range
                  </h4>
                  <div className={styles.priceRangeContainer}>
                    <PriceRangeSlider
                      min={availableFilters.priceRange.minPrice}
                      max={availableFilters.priceRange.maxPrice}
                      values={priceRange}
                      onChange={handlePriceChange}
                      className={styles.priceSlider}
                    />
                    <div className={styles.priceRangeValues}>
                      <span>₹{priceRange[0]?.toLocaleString() || 0}</span>
                      <span>₹{priceRange[1]?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Color Filter */}
                {availableFilters.colors.length > 0 && (
                  <div className={styles.filterSection}>
                    <h4 className={styles.filterSectionTitle}>
                      <FontAwesomeIcon icon={faPalette} /> Color
                    </h4>
                    <div className={styles.colorSelectContainer}>
                      <Select
                        isMulti
                        options={colorOptions}
                        value={selectedColorValues}
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : [];
                          setSelectedColors(selectedValues);
                        }}
                        className={styles.colorSelect}
                        classNamePrefix="color-select"
                        placeholder="Search and select colors..."
                        noOptionsMessage={({ inputValue }) =>
                          inputValue ? "No colors found" : "No colors available"
                        }
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        isSearchable={true}
                        isClearable={false}
                        menuPortalTarget={document.body} // Add this line
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Add this style
                          control: (base, state) => ({
                            ...base,
                            border: `2px solid ${
                              state.isFocused
                                ? "var(--accent-warm)"
                                : "var(--border-medium)"
                            }`,
                            borderRadius: "var(--border-radius-sm)",
                            padding: "4px 8px",
                            backgroundColor: "var(--glass-bg)",
                            boxShadow: state.isFocused
                              ? "0 0 0 3px rgba(212, 165, 116, 0.1)"
                              : "var(--shadow-sm)",
                            minHeight: "52px",
                            transition: "var(--transition)",
                            "&:hover": {
                              borderColor: state.isFocused
                                ? "var(--accent-warm)"
                                : "var(--accent-warm)",
                            },
                          }),
                          menu: (base) => ({
                            ...base,
                            borderRadius: "var(--border-radius)",
                            border: "1px solid var(--border-light)",
                            boxShadow: "var(--shadow-lg)",
                            overflow: "hidden",
                            backgroundColor: "var(--glass-bg)",
                            backdropFilter: "blur(12px)",
                            zIndex: 9999, // Ensure high z-index
                          }),
                          // ... keep the rest of your styles the same
                        }}
                        components={{
                          MultiValueContainer: ({ children }) => (
                            <div className={styles.multiValueContainer}>
                              {children}
                            </div>
                          ),
                          MultiValueRemove: ({ innerProps }) => (
                            <div
                              {...innerProps}
                              className={styles.multiValueRemove}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </div>
                          ),
                          IndicatorSeparator: () => null,
                          DropdownIndicator: () => (
                            <div className={styles.dropdownIndicator}>
                              <FontAwesomeIcon icon={faChevronDown} />
                            </div>
                          ),
                          ClearIndicator: () => null,
                        }}
                      />
                    </div>

                    {/* Selected colors summary */}
                    {selectedColors.length > 0 && (
                      <div className={styles.selectedColorsSummary}>
                        <div className={styles.selectedColorsHeader}>
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className={styles.selectedIcon}
                          />
                          <span>
                            {selectedColors.length} color
                            {selectedColors.length !== 1 ? "s" : ""} selected
                          </span>
                          <button
                            className={styles.clearColorsButton}
                            onClick={clearSelectedColors}
                          >
                            Clear all
                          </button>
                        </div>
                        <div className={styles.selectedColorsList}>
                          {selectedColors.slice(0, 8).map((color) => {
                            const colorData = availableFilters.colors.find(
                              (c) => c.value === color
                            );
                            return (
                              <span
                                key={color}
                                className={styles.selectedColorTag}
                              >
                                {colorData?.label || color}
                                <button
                                  onClick={() => handleColorChange(color)}
                                  className={styles.removeColorButton}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </span>
                            );
                          })}
                          {selectedColors.length > 8 && (
                            <span className={styles.moreColorsTag}>
                              +{selectedColors.length - 8} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Series Filter */}
                {availableFilters.series.length > 0 && (
                  <div className={styles.filterSection}>
                    <h4 className={styles.filterSectionTitle}>
                      <FontAwesomeIcon icon={faLayerGroup} /> Product Series
                    </h4>
                    <SeriesFilter
                      series={availableFilters.series.map((series) => ({
                        _id: series.value,
                        id: series.value,
                        title: series.label,
                      }))}
                      selectedSeries={selectedSeries}
                      onChange={handleSeriesChange}
                    />
                  </div>
                )}

                {/* Sort Options */}
                <div className={styles.filterSection}>
                  <h4 className={styles.filterSectionTitle}>
                    <FontAwesomeIcon icon={faSortAmountDown} /> Sort By
                  </h4>
                  <Select
                    className={styles.select}
                    classNamePrefix="select"
                    options={sortOptions}
                    value={sortOptions.find((opt) => opt.value === sortOption)}
                    onChange={handleSortChange}
                    isSearchable={false}
                    aria-label="Select sort option"
                  />
                </div>
              </div>

              {/* Mobile Apply Filters Button */}
              <div className={styles.mobileApplyButton}>
                <button
                  className={styles.applyFiltersButton}
                  onClick={() => setMobileFiltersOpen(false)}
                >
                  Apply Filters
                </button>
              </div>
            </aside>
          )}

          {/* Products Area */}
          <main className={styles.productsArea}>
            {/* Mobile Filter Header */}
            <div className={styles.mobileFilterHeader}>
              <button
                className={styles.mobileFilterButton}
                onClick={() => setMobileFiltersOpen(true)}
                aria-label="Open filters"
              >
                <FontAwesomeIcon icon={faFilter} />
                Filters
                {appliedFiltersCount > 0 && (
                  <span className={styles.filterBadge}>
                    {appliedFiltersCount}
                  </span>
                )}
              </button>

              <div className={styles.viewOptions}>
                <label>Show:</label>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className={styles.pageSizeSelect}
                  aria-label="Items per page"
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={36}>36</option>
                  <option value={48}>48</option>
                </select>
              </div>
            </div>

            {/* Active Filters Display */}
            {appliedFiltersCount > 0 && (
              <div className={styles.activeFilters}>
                <div className={styles.activeFiltersHeader}>
                  <span>Active Filters:</span>
                  <button onClick={resetFilters}>Clear All</button>
                </div>
                <div className={styles.activeFiltersList}>
                  {searchQuery && (
                    <span className={styles.activeFilter}>
                      Search: "{searchQuery}"
                      <button onClick={() => setSearchQuery("")}>×</button>
                    </span>
                  )}
                  {selectedDiscountRange !== "all" && (
                    <span className={styles.activeFilter}>
                      Discount:{" "}
                      {
                        discountRanges.find(
                          (r) => r.value === selectedDiscountRange
                        )?.label
                      }
                      <button onClick={() => setSelectedDiscountRange("all")}>
                        ×
                      </button>
                    </span>
                  )}
                  {(priceRange[0] > availableFilters.priceRange.minPrice ||
                    priceRange[1] < availableFilters.priceRange.maxPrice) && (
                    <span className={styles.activeFilter}>
                      Price: ₹{priceRange[0]?.toLocaleString()} - ₹
                      {priceRange[1]?.toLocaleString()}
                      <button
                        onClick={() =>
                          setPriceRange([
                            availableFilters.priceRange.minPrice,
                            availableFilters.priceRange.maxPrice,
                          ])
                        }
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {selectedSeries && (
                    <span className={styles.activeFilter}>
                      Series:{" "}
                      {
                        availableFilters.series.find(
                          (s) => s.value === selectedSeries
                        )?.label
                      }
                      <button onClick={() => setSelectedSeries(null)}>×</button>
                    </span>
                  )}
                  {selectedColors.map((color) => (
                    <span key={color} className={styles.activeFilter}>
                      Color: {color}
                      <button onClick={() => handleColorChange(color)}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Products Grid */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`${currentPage}-${searchQuery}-${selectedDiscountRange}`}
                className={styles.productsGrid}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {products?.length > 0 ? (
                  products.map((product) => (
                    <DiscountProductCard key={product._id} product={product} />
                  ))
                ) : (
                  <div className={styles.noProducts}>
                    <div className={styles.noProductsIcon}>🔍</div>
                    <h3>No products found</h3>
                    <p>Try adjusting your filters or search terms</p>
                    <button
                      className={styles.resetFiltersButton}
                      onClick={resetFilters}
                    >
                      Reset All Filters
                    </button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Pagination */}
            {totalProducts > pageSize && (
              <div className={styles.paginationContainer}>
                <ReactPaginate
                  previousLabel={<FontAwesomeIcon icon={faArrowLeft} />}
                  nextLabel={<FontAwesomeIcon icon={faArrowRight} />}
                  pageCount={Math.ceil(totalProducts / pageSize)}
                  onPageChange={handlePageClick}
                  containerClassName={styles.pagination}
                  previousLinkClassName={styles.paginationLink}
                  nextLinkClassName={styles.paginationLink}
                  disabledClassName={styles.paginationDisabled}
                  activeClassName={styles.paginationActive}
                  forcePage={currentPage}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageClassName={styles.paginationItem}
                  pageLinkClassName={styles.paginationPageLink}
                />
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Floating Filter Button */}
      <button
        className={styles.floatingFilterButton}
        onClick={() => setMobileFiltersOpen(true)}
        aria-label="Open filters"
      >
        <FontAwesomeIcon icon={faFilter} />
        {appliedFiltersCount > 0 && (
          <span className={styles.floatingFilterBadge}>
            {appliedFiltersCount}
          </span>
        )}
      </button>

      {/* Mobile Overlay */}
      {mobileFiltersOpen && (
        <div
          className={styles.mobileOverlay}
          onClick={() => setMobileFiltersOpen(false)}
        />
      )}
    </div>
  );
};

export default DiscountProductPage;
